import { buildApiUrl } from "../../utils/api";
import { matchesProductSearch } from "../../utils/matchesProductSearch";
import { getActivePincode } from "../../utils/location";
import { normalizeProductCard } from "../../utils/normalizeProductCard";



export const createProductsSlice = (set, get) => ({
  products: [],
  products_loading: false,
  products_loaded: false,
  products_error: null,
  activeCategorySlug: null,
  _fetchRequestId: 0,
  sort_by: "relevant",
  min_price: 0,
  max_price: 10000,
  filters: {},

  /**
   * Fetch the product catalog from GET /api/v1/customer/products/list
   *
   * @param {string|null} categorySlug  - Category slug or null for all products
   * @param {object}      location      - Optional location context
   *   { pincode, latitude, longitude, address_id }
   *   Any supplied values are forwarded to the API so it returns store-specific stock.
   *   Priority follows the backend resolution hierarchy:
   *   address_id > lat/lng > pincode
   */
  fetchProducts: async (categorySlug = null, location = {}) => {
    const requestId = (get()._fetchRequestId || 0) + 1;
    const normalizedSlug = categorySlug && categorySlug !== 'all' ? String(categorySlug).trim() : null;

    set({
      _fetchRequestId: requestId,
      activeCategorySlug: normalizedSlug,
      products_loading: true,
      products_loaded: false,
      products: [],
      products_error: null,
    });

    try {
      // Build query params — always include pagination
      const params = new URLSearchParams({ page: '1', limit: '100' });
      if (normalizedSlug) params.set('category', normalizedSlug);

      // Attach location context — pass pincode to catalog endpoint
      const loc = location || {};
      const resolvedPin = getActivePincode(get()) || loc.pincode;
      if (resolvedPin) {
        params.set('pincode', String(resolvedPin));
      }

      const url = buildApiUrl(`/api/v1/customer/products/list?${params.toString()}`);
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch products");

      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Failed to fetch products");

      // Stale request guard: if another fetchProducts has been initiated since, discard this response
      if (get()._fetchRequestId !== requestId) {
        return [];
      }

      const rawList = data.data?.products ?? data.data ?? [];
      const products = rawList.map(normalizeProductCard);
      const highestPrice = products.reduce((max, p) => p.price > max ? p.price : max, 0);

      set({
        products,
        products_loading: false,
        products_loaded: true,
        max_price: highestPrice || 10000,
      });
      return products;
    } catch (error) {
      if (get()._fetchRequestId === requestId) {
        set({
          products_error: error.message,
          products_loading: false,
          products_loaded: true,
          products: [],
        });
      }
      console.error("Error fetching products:", error);
      return [];
    }
  },

  getFilteredProducts: () => {
    const {
      products,
      searchQuery,
      sort_by,
      min_price,
      max_price,
      filters,
    } = get();
    let filtered = products;

    // No client-side category filter — API already returns category-filtered products

    if (searchQuery?.trim()) {
      filtered = filtered.filter((product) =>
        matchesProductSearch(product, searchQuery),
      );
    }

    filtered = filtered.filter(
      (product) => product.price >= min_price && product.price <= max_price,
    );

    if (Object.keys(filters).length > 0) {
      filtered = filtered.filter((product) => {
        return Object.keys(filters).every((key) => {
          const filterValues = filters[key];
          return filterValues.includes(product[key]);
        });
      });
    }

    switch (sort_by) {
      case "price_low_to_high":
        filtered.sort((a, b) => a.price - b.price);
        break;
      case "price_high_to_low":
        filtered.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        filtered.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        break;
      case "rating":
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        // relevant - keep original order
        break;
    }

    return filtered;
  },

  setSortBy: (sort) => set({ sort_by: sort }),
  setPriceRange: (min, max) => set({ min_price: min, max_price: max }),
  setFilters: (filters) => set({ filters }),
  resetFilters: () =>
    set({
      sort_by: "relevant",
      min_price: 0,
      max_price: 10000,
      filters: {},
    }),

  // Synchronously wipe the current product list and set loading state.
  // Called before paint when switching categories so stale products never render.
  resetProducts: (categorySlug = null) => {
    const requestId = (get()._fetchRequestId || 0) + 1;
    const normalizedSlug = categorySlug && categorySlug !== 'all' ? String(categorySlug).trim() : null;
    set({
      _fetchRequestId: requestId,
      activeCategorySlug: normalizedSlug,
      products: [],
      products_loading: true,
      products_loaded: false,
      products_error: null,
    });
  },
});
